import { OskCurrencyPipe } from '@shared/pipes/osk-currency.pipe';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AdminMenuItemStore } from '@features/admin';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-admin-menu-items',
  standalone: true,
  imports: [SharedModule, FormsModule, ReactiveFormsModule, OskCurrencyPipe],
  templateUrl: 'admin-menu-items.component.html',
  styleUrl: 'admin-menu-items.component.scss',
})
export class AdminMenuItemsComponent implements OnInit {
  readonly store = inject(AdminMenuItemStore);
  searchTerm = '';
  restaurantFilter = '';
  categoryFilter = '';
  statusFilter = '';
  viewMode = signal<'grid' | 'list'>('grid');
  showModal = signal(false);
  editingItem = signal<any>(null);
  imagePreview = signal<string | null>(null);
  selectedAllergens = signal<string[]>([]);
  availableAllergens = [
    'Gluten',
    'Crustacés',
    'Œufs',
    'Poisson',
    'Arachides',
    'Soja',
    'Lait',
    'Fruits à coque',
    'Céleri',
    'Moutarde',
    'Sésame',
    'Sulfites',
    'Lupin',
    'Mollusques',
  ];
  filteredItems = computed(() => {
    let items = this.store.items();

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(term) ||
          item.description?.toLowerCase().includes(term),
      );
    }

    if (this.restaurantFilter) {
      items = items.filter(
        (item) => item.restaurantId === Number(this.restaurantFilter),
      );
    }

    if (this.categoryFilter) {
      items = items.filter(
        (item) => item.categoryId === Number(this.categoryFilter),
      );
    }

    if (this.statusFilter) {
      switch (this.statusFilter) {
        case 'available':
          items = items.filter((item) => item.available && !item.outOfStock);
          break;
        case 'unavailable':
          items = items.filter((item) => !item.available);
          break;
        case 'outOfStock':
          items = items.filter((item) => item.outOfStock);
          break;
      }
    }

    return items;
  });
  filteredCategories = computed(() => {
    if (!this.restaurantFilter) {
      return this.store.categories();
    }
    return this.store
      .categories()
      .filter((c) => c.restaurantId === Number(this.restaurantFilter));
  });
  formCategories = computed(() => {
    const restaurantId = this.itemForm.get('restaurantId')?.value;
    if (!restaurantId) {
      return [];
    }
    return this.store
      .categories()
      .filter((c) => c.restaurantId === Number(restaurantId));
  });
  private readonly fb = inject(FormBuilder);
  itemForm: FormGroup = this.fb.group({
    restaurantId: ['', Validators.required],
    categoryId: ['', Validators.required],
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
    price: [0, [Validators.required, Validators.min(0)]],
    discountPrice: [null],
    available: [true],
    featured: [false],
    vegetarian: [false],
    vegan: [false],
    glutenFree: [false],
    calories: [null],
    preparationTime: [null],
  });

  ngOnInit(): void {
    this.store.loadItems();
    this.store.loadRestaurants();
    this.store.loadCategories();
  }

  onSearch(term: string): void {
    // Filtrage géré par computed
  }

  onFilterChange(): void {
    // Filtrage géré par computed
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
  }

  openCreateModal(): void {
    this.editingItem.set(null);
    this.selectedAllergens.set([]);
    this.imagePreview.set(null);
    this.itemForm.reset({
      available: true,
      featured: false,
      vegetarian: false,
      vegan: false,
      glutenFree: false,
      price: 0,
    });
    this.showModal.set(true);
  }

  editItem(item: any): void {
    this.editingItem.set(item);
    this.selectedAllergens.set(item.allergens || []);
    this.imagePreview.set(item.imageUrl);
    this.itemForm.patchValue({
      restaurantId: item.restaurantId,
      categoryId: item.categoryId,
      name: item.name,
      description: item.description || '',
      price: item.price,
      discountPrice: item.discountPrice,
      available: item.available,
      featured: item.featured,
      vegetarian: item.vegetarian,
      vegan: item.vegan,
      glutenFree: item.glutenFree,
      calories: item.calories,
      preparationTime: item.preparationTime,
    });
    this.showModal.set(true);
  }

  duplicateItem(item: any): void {
    this.editingItem.set(null);
    this.selectedAllergens.set(item.allergens || []);
    this.imagePreview.set(item.imageUrl);
    this.itemForm.patchValue({
      restaurantId: item.restaurantId,
      categoryId: item.categoryId,
      name: `${item.name} (copie)`,
      description: item.description || '',
      price: item.price,
      discountPrice: item.discountPrice,
      available: true,
      featured: false,
      vegetarian: item.vegetarian,
      vegan: item.vegan,
      glutenFree: item.glutenFree,
      calories: item.calories,
      preparationTime: item.preparationTime,
    });
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingItem.set(null);
  }

  saveItem(): void {
    if (this.itemForm.valid) {
      const formValue = {
        ...this.itemForm.value,
        allergens: this.selectedAllergens(),
      };

      if (this.editingItem()) {
        this.store.updateItem({
          id: this.editingItem().id,
          ...formValue,
        });
      } else {
        this.store.createItem(formValue);
      }

      this.closeModal();
    }
  }

  deleteItem(item: any): void {
    if (confirm(`Êtes-vous sûr de vouloir supprimer "${item.name}" ?`)) {
      this.store.deleteItem(item.id);
    }
  }

  toggleAvailability(item: any): void {
    this.store.updateItem({
      ...item,
      available: !item.available,
    });
  }

  onRestaurantChange(restaurantId: any): void {
    this.itemForm.patchValue({ categoryId: '' });
  }

  onImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  toggleAllergen(allergen: string): void {
    const current = this.selectedAllergens();
    if (current.includes(allergen)) {
      this.selectedAllergens.set(current.filter((a) => a !== allergen));
    } else {
      this.selectedAllergens.set([...current, allergen]);
    }
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.store.totalPages()) {
      this.store.loadItems({ page });
    }
  }

  getStatusClass(item: any): string {
    if (item.outOfStock) return 'out-of-stock';
    return item.available ? 'available' : 'unavailable';
  }

  getStatusLabel(item: any): string {
    if (item.outOfStock) return 'Rupture';
    return item.available ? 'Disponible' : 'Indisponible';
  }
}
